import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import DataTable from "react-data-table-component";
import { Button } from "reactstrap";
import { Link } from "react-router-dom";
import "./index.scss";
import TopNav from "views/Components/SuperAdmin/Navbars/TopNav/index";
import Sidenav from "views/Components/SuperAdmin/Navbars/Sidebar/index";
import { API_BASE_URL } from "constants/index";

const tableStyles = {
  headCells: {
    style: {
      fontWeight: "600",
      fontSize: "1.4rem",
      textTransform: "capitalize",
      paddingLeft: "1rem",
      paddingRight: "1rem",
    },
  },
  cells: { style: { paddingLeft: "1rem", paddingRight: "1rem" } },
  rows: { style: { minHeight: "5.2rem" } },
};

const getAuthConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.token}` },
});

const getTokenSummary = (usage) =>
  usage
    ? `${usage.input_tokens || 0} in / ${usage.output_tokens || 0} out / ${usage.total_tokens || 0} total`
    : "-";

const PAGE_SIZE = 50;

const AIComplianceLogs = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/admin/ai-compliance-logs`,
        {
          ...getAuthConfig(),
          params: { offset, limit: PAGE_SIZE },
        },
      );
      const data = response.data?.data || {};
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (error) {
      setMessage(
        error?.response?.data?.message || "Unable to load AI compliance logs.",
      );
    } finally {
      setLoading(false);
    }
  }, [offset]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const viewLog = async (logId) => {
    setDetailLoading(true);
    setMessage("");
    try {
      const response = await axios.get(
        `${API_BASE_URL}/admin/ai-compliance-logs/${logId}`,
        getAuthConfig(),
      );
      setSelectedLog(response.data?.data || null);
    } catch (error) {
      setMessage(
        error?.response?.data?.message ||
          "Unable to load AI compliance log details.",
      );
    } finally {
      setDetailLoading(false);
    }
  };

  const columns = [
    {
      name: "Requested",
      selector: (log) => new Date(log.created_at).toLocaleString(),
      sortable: true,
    },
    { name: "User ID", selector: (log) => log.user_id || "-", sortable: true },
    {
      name: "Organization ID",
      selector: (log) => log.organization_id || "-",
      sortable: true,
    },
    {
      name: "Financial year",
      selector: (log) => log.financial_year || "-",
      sortable: true,
    },
    {
      name: "Laws",
      selector: (log) =>
        log.selected_laws
          ?.map((law) => `${law.name} v${law.version_number}`)
          .join(", ") || "-",
      grow: 2,
    },
    { name: "Status", selector: (log) => log.status, sortable: true },
    { name: "Tokens", selector: (log) => getTokenSummary(log.usage), grow: 2 },
    {
      name: "Actions",
      center: true,
      cell: (log) => (
        <Button color="primary" size="sm" onClick={() => viewLog(log.id)}>
          View details
        </Button>
      ),
    },
  ];

  return (
    <>
      <TopNav />
      <Sidenav />
      <main className="ai-compliance-logs">
        <div className="listing__breadcrum">
          <span className="font-14">
            <Link className="op-2" to="/app/admin/dashboard">
              Home/ Dashboard/{" "}
            </Link>
            <span className="c__blue">AI Logs</span>
          </span>
        </div>
        <section className="ai-compliance-logs__panel">
          <div className="ai-compliance-logs__header">
            <div>
              <h3>AI Compliance Logs</h3>
              <p>
                Immutable request and response records for every OpenAI
                compliance review.
              </p>
            </div>
            <Button color="secondary" onClick={loadLogs} disabled={loading}>
              Refresh
            </Button>
          </div>
          {message && <p className="ai-compliance-logs__message">{message}</p>}
          <DataTable
            columns={columns}
            data={logs}
            customStyles={tableStyles}
            highlightOnHover
            progressPending={loading}
            noDataComponent="No AI compliance logs found."
          />
          <div className="ai-compliance-logs__pagination">
            <Button
              color="secondary"
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              disabled={offset === 0 || loading}
            >
              Previous
            </Button>
            <span>
              {total
                ? `${offset + 1}-${Math.min(offset + PAGE_SIZE, total)} of ${total}`
                : "0 logs"}
            </span>
            <Button
              color="secondary"
              onClick={() => setOffset(offset + PAGE_SIZE)}
              disabled={offset + PAGE_SIZE >= total || loading}
            >
              Next
            </Button>
          </div>
        </section>
        {(detailLoading || selectedLog) && (
          <section className="ai-compliance-logs__detail">
            <div className="ai-compliance-logs__header">
              <h3>Log details</h3>
              <Button
                color="secondary"
                onClick={() => setSelectedLog(null)}
                disabled={detailLoading}
              >
                Close
              </Button>
            </div>
            {detailLoading ? (
              <p>Loading complete request and response…</p>
            ) : (
              <pre>{JSON.stringify(selectedLog, null, 2)}</pre>
            )}
          </section>
        )}
      </main>
    </>
  );
};

export default AIComplianceLogs;
